import { useState, useEffect, memo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Map as MapIcon, User as UserIcon, Rocket, Users, Newspaper, Share2, ShoppingBag, ShoppingCart, Sparkles } from 'lucide-react';
import { useAuth } from "../context/AuthContext";
import AuthModal from './AuthModal';
import WhatsNewModal from './WhatsNewModal';
import { api } from '../services/api';
import { AnimatePresence, motion } from 'framer-motion';
import NotificationBell from './NotificationBell';
import InviteModal from './Sharing/InviteModal';
import BetaWarning from './BetaWarning';
import { useTranslation } from 'react-i18next';
import AlphaBanner from './AlphaBanner';
import ContactModalWrapper from './Landing/ContactModalWrapper';
import { useCart } from '../context/CartContext';
import CartDrawer from './CartDrawer';
import MobileVideoSplash from './MobileVideoSplash';

// Memoized Bottom Navigation
const BottomNav = memo(({ isActive, itemsCount }: { isActive: (p: string) => boolean, itemsCount: number }) => {
    const { t } = (useTranslation as any)();
    return (
        <nav className="w-full bg-white border-t border-slate-200 flex justify-between px-1 pt-0.5 pb-safe z-40 shadow-[0_-8px_15px_-3px_rgba(0,0,0,0.05)]">
            <Link to="/app/map" className={`flex flex-col items-center flex-1 min-w-0 transition ${isActive('/app/map') ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
                <MapIcon size={24} strokeWidth={isActive('/app/map') ? 2.5 : 2} />
                <span className="text-[10px] mt-0 font-medium truncate w-full text-center">{t('nav.map')}</span>
            </Link>
            <Link to="/app/search" className={`flex flex-col items-center flex-1 min-w-0 transition ${isActive('/app/search') ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
                <Search size={24} strokeWidth={isActive('/app/search') ? 2.5 : 2} />
                <span className="text-[10px] mt-0 font-medium truncate w-full text-center">{t('nav.search')}</span>
            </Link>
            <Link to="/app/feed" className={`flex flex-col items-center flex-1 min-w-0 transition ${isActive('/app/feed') ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
                <Newspaper size={24} strokeWidth={isActive('/app/feed') ? 2.5 : 2} />
                <span className="text-[10px] mt-0 font-medium truncate w-full text-center">{t('nav.feed')}</span>
            </Link>

            <Link to="/app/home" className="flex flex-col items-center justify-end w-16 relative group -mt-10 mx-1 z-10">
                <div className="w-16 h-16 bg-transparent border-4 border-slate-50 rounded-full shadow-2xl shadow-blue-900/40 transition transform group-active:scale-95 flex items-center justify-center overflow-hidden bg-white">
                    <img src="/logo-v2.webp" alt={t('nav.home')} className="w-full h-full object-cover scale-150" />
                </div>
            </Link>

            <Link to="/app/circles" className={`flex flex-col items-center flex-1 min-w-0 transition ${isActive('/app/circles') ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
                <Users size={24} strokeWidth={isActive('/app/circles') ? 2.5 : 2} />
                <span className="text-[10px] mt-0 font-medium truncate w-full text-center">{t('nav.circles')}</span>
            </Link>

            <Link to="/app/match" className={`flex flex-col items-center flex-1 min-w-0 transition ${isActive('/app/match') ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
                <Sparkles size={24} strokeWidth={isActive('/app/match') ? 2.5 : 2} />
                <span className="text-[10px] mt-0 font-medium truncate w-full text-center">{t('nav.match')}</span>
            </Link>

            <Link to="/app/marketplace" className={`flex flex-col items-center flex-1 min-w-0 transition ${isActive('/app/marketplace') ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}>
                <div className="relative">
                    <ShoppingBag size={24} strokeWidth={isActive('/app/marketplace') ? 2.5 : 2} />
                    {itemsCount > 0 && (
                        <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center border border-white shadow-sm">
                            {itemsCount}
                        </span>
                    )}
                </div>
                <span className="text-[10px] mt-0 font-medium truncate w-full text-center">{t('nav.marketplace')}</span>
            </Link>
        </nav>
    );
});

export default function Layout() {
    const { t } = (useTranslation as any)(['common', 'profile', 'notifications']);
    const { user, login, activeAuthModal, openAuthModal, closeAuthModal } = useAuth();
    const { items } = useCart();
    const location = useLocation();
    const navigate = useNavigate();
    const [isCartOpen, setIsCartOpen] = useState(false);
    const itemsCount = items.reduce((acc, i) => acc + i.quantity, 0);
    // Removed local auth state
    const [isChecking, setIsChecking] = useState(true); // Prevent flash of content
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showContact, setShowContact] = useState(false);
    const [showSplash, setShowSplash] = useState(() => {
        // Only show once per session
        return !sessionStorage.getItem('splash_shown');
    });

    const isActive = (path: string) => location.pathname === path;

    const handleSplashClose = () => {
        sessionStorage.setItem('splash_shown', 'true');
        setShowSplash(false);
    };

    useEffect(() => {
        if (user) {
            const isPro = ['manager', 'club_manager', 'merchant', 'staff', 'club_staff', 'coach', 'merchant_staff', 'federation_admin', 'federation_staff'].includes(user.role || '');

            // New logic: Check DB-backed status
            // If pro, check manager_onboarding_done. If player (or pro who plays), check onboarding_done?
            // Actually, we want to respect the 'needs_onboarding' localStorage only for INITIAL registration flow,
            // but for safety net, we should check if they REALLY need it.

            const needsOnboarding = localStorage.getItem('needs_onboarding') === 'true';

            if (needsOnboarding) {
                if (isPro && !user.manager_onboarding_done) {
                    console.log("SAFETY NET (Layout): Redirecting pro to /pro/welcome");
                    navigate('/pro/welcome');
                } else if (!isPro && !user.onboarding_done) {
                    console.log("SAFETY NET (Layout): Redirecting player to /onboarding");
                    navigate('/onboarding');
                } else {
                    // Logic: If they have the flag but are actually done in DB, clear flag and allow
                    localStorage.removeItem('needs_onboarding');
                    setIsChecking(false);
                }
            } else {
                setIsChecking(false);
            }
        } else {
            setIsChecking(false);
        }
    }, [user, navigate]);

    if (isChecking && user && localStorage.getItem('needs_onboarding') === 'true') {
        return <div className="h-screen bg-slate-900 flex items-center justify-center text-white">{t('status.redirecting')}</div>;
    }

    const handleLogin = async (mode: 'login' | 'register' | 'admin-login', email: string, pass: string, pseudo?: string, _termsAccepted?: boolean, _clubClaim?: { clubId: string, proofUrl: string }, _newClubData?: { name: string, address: string, city: string, zip_code: string, country: string, website: string, proofUrl: string }, managerDetails?: { firstName: string, lastName: string }, role?: string) => {
        let data;
        if (mode === 'login' || mode === 'admin-login') {
            data = await api.auth.login({ email, password: pass });
        } else {
            // Register logic via Layout (e.g. from Profile > Login > Register)
            data = await api.auth.register({
                email,
                password: pass,
                display_name: pseudo || email.split('@')[0],
                first_name: managerDetails?.firstName,
                last_name: managerDetails?.lastName,
                role: role || 'player',
                terms_accepted: true
            });
            localStorage.setItem('needs_onboarding', 'true');
        }
        login(data.token, data.user);

        // Redirect logic
        if (mode === 'register') {
            const isPro = ['manager', 'club_manager', 'merchant', 'staff', 'club_staff', 'coach', 'merchant_staff', 'federation_admin', 'federation_staff'].includes(data.user?.role || '');
            console.log("LAYOUT: Redirecting to Onboarding. IsPro:", isPro);
            window.location.href = isPro ? '/pro/welcome' : '/onboarding';
        } else {
            if (location.pathname === '/' || location.pathname === '/intro') navigate('/app/feed');
        }
    };

    // Removed openAuth local function, using openAuthModal from context directly

    const isImmersivePage = location.pathname.includes('/tournaments/') || location.pathname.includes('/match/') || location.pathname.includes('/marketplace');
    console.log('[Layout] Path:', location.pathname, 'Immersive:', isImmersivePage);

    return (
        <>
            <div className="fixed inset-0 flex flex-col bg-slate-900 overflow-hidden">
                {/* Header / Banner wrapper wrapper with safe area awareness */}
                {!isImmersivePage && (
                    <div className="pt-safe transition-colors duration-300 bg-slate-900" style={{
                        backgroundColor: showSplash ? '#0f172a' : (localStorage.getItem('isQuotaExceeded') === 'true' ? '#dc2626' : '#0f172a')
                    }}>
                        <AlphaBanner onContactClick={() => setShowContact(true)} />
                        <BetaWarning />
                        <header className="bg-slate-900 border-b border-slate-800 text-white p-4 shadow-md z-10 flex justify-between items-center">
                            <div className="text-xl font-extrabold tracking-tighter text-white">
                                PADEL<span className="text-yellow-400">PARTNER</span>
                            </div>

                            <div className="flex items-center gap-4">
                                {user && (
                                    <>
                                        <button
                                            onClick={() => setShowInviteModal(true)}
                                            className="w-8 h-8 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center text-slate-300 hover:text-white transition"
                                        >
                                            <Share2 size={16} />
                                        </button>
                                        <Link
                                            to="/app/discover"
                                            className="w-8 h-8 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center text-slate-300 hover:text-white transition"
                                            title={t('nav.discover')}
                                        >
                                            <Rocket size={16} />
                                        </Link>
                                        <button
                                            onClick={() => setIsCartOpen(true)}
                                            className="relative w-8 h-8 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center text-slate-300 hover:text-white transition"
                                        >
                                            <ShoppingCart size={16} />
                                            {itemsCount > 0 && (
                                                <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] font-black w-4 h-4 rounded-full flex items-center justify-center border-2 border-slate-900 shadow-sm">
                                                    {itemsCount}
                                                </span>
                                            )}
                                        </button>
                                        <NotificationBell />
                                    </>
                                )}

                                {user ? (
                                    <Link to="/app/profile" className="w-8 h-8 rounded-full bg-slate-800 border-2 border-slate-700 overflow-hidden flex items-center justify-center">
                                        {user.profile_pic_url ? (
                                            <img
                                                src={user.profile_pic_url}
                                                alt={t('profile_picture')}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.currentTarget.onerror = null;
                                                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${user.display_name}&background=0f172a&color=fff`;
                                                }}
                                            />
                                        ) : (
                                            <UserIcon size={16} className="text-slate-400" />
                                        )}
                                    </Link>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => openAuthModal('login')}
                                            className="text-[11px] font-bold text-slate-400 hover:text-white transition uppercase tracking-wider"
                                        >
                                            {t('actions.login')}
                                        </button>
                                        <button
                                            onClick={() => openAuthModal('register')}
                                            className="text-[11px] font-bold bg-yellow-400 text-slate-900 px-4 py-2 rounded-full hover:bg-yellow-300 transition shadow-lg shadow-yellow-400/10 uppercase tracking-wider"
                                        >
                                            {t('actions.register')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </header>
                    </div>
                )}

                {/* Global Modals */}
                <WhatsNewModal />
                <InviteModal isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} />

                <AnimatePresence>
                    {showContact && (
                        <ContactModalWrapper onClose={() => setShowContact(false)} />
                    )}
                </AnimatePresence>

                {/* Main Content */}
                <main className={`flex-1 relative bg-slate-50 ${location.pathname.includes('/map') ? 'overflow-hidden p-0 h-full' : 'overflow-y-auto overflow-x-hidden scroll-smooth'}`}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={location.pathname}
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            transition={{ duration: 0.15 }}
                            className={location.pathname.includes('/map') ? 'absolute inset-0' : 'min-h-full'}
                        >
                            <Outlet />
                        </motion.div>
                    </AnimatePresence>
                </main>

                <BottomNav isActive={isActive} itemsCount={itemsCount} />

                <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

                <AnimatePresence>
                    {activeAuthModal && (
                        <AuthModal
                            isOpen={!!activeAuthModal}
                            onClose={closeAuthModal}
                            defaultMode={activeAuthModal}
                            onLogin={handleLogin}
                        />
                    )}
                </AnimatePresence>
            </div>

            {/* Mobile Video Splash - Outside main layout for visibility detection */}
            <AnimatePresence mode="wait">
                {showSplash && (
                    <MobileVideoSplash onClose={handleSplashClose} />
                )}
            </AnimatePresence>
        </>
    );
}
